import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Printer, TrendingUp, PackageCheck, AlertTriangle, XCircle, Trash2 } from "lucide-react";

export default function SuperAdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [bills, setBills] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    try {
      const [{ data: o }, { data: t }, { data: b }, { data: a }] = await Promise.all([
        api.get("/ops/overview"),
        api.get("/trips?status=draft"),
        api.get("/bills?status=pending_approval"),
        api.get("/ops/analytics"),
      ]);
      setOverview(o); setDrafts(t.trips); setBills(b.bills); setAnalytics(a);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const approveTrip = async (id) => {
    try { await api.post(`/trips/${id}/approve`); toast.success("Trip approved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approveBill = async (id) => {
    try { await api.post(`/bills/${id}/approve`); toast.success("Bill approved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteBill = async (b) => {
    if (!window.confirm(`Move bill ${b.bill_no} to the recycle bin? Its ${b.trips?.length || 0} trip(s) will become unbilled again. You can restore it within 90 days.`)) return;
    try { await api.delete(`/bills/${b.id}`); toast.success(`Bill ${b.bill_no} moved to recycle bin`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="overline text-[#FF5A00]">SUPER ADMIN · APPROVALS</div>
        <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="superadmin-dashboard">Approve. Control. Ship.</h1>

        {overview && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Draft trips" value={overview.trips.draft} accent />
            <Stat label="Bills pending" value={overview.bills.pending} accent />
            <Stat label="In transit" value={overview.trips.in_transit} />
            <Stat label="Revenue" value={formatINR(overview.revenue_all_time_inr || 0)} />
          </div>
        )}

        <Tabs defaultValue="trips" className="mt-10">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="trips" data-testid="sa-tab-trips" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Trips awaiting approval ({drafts.length})</TabsTrigger>
            <TabsTrigger value="bills" data-testid="sa-tab-bills" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Bills awaiting approval ({bills.length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="sa-tab-analytics" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="trips" className="mt-6">
            <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr><th className="text-left p-3">CN #</th><th className="text-left p-3">Type</th><th className="text-left p-3">Route</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Freight</th><th className="text-left p-3">Created by</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {drafts.map(t => (
                    <tr key={t.id} data-testid={`sa-trip-row-${t.id}`} className="border-t border-[#2A2A2A]">
                      <td className="p-3 font-mono-ll text-xs">{t.cn_number}</td>
                      <td className="p-3 text-xs">{t.trip_type === "driver_placement" ? "Driver Hire" : "Vehicle"}</td>
                      <td className="p-3 font-mono-ll text-xs inline-flex items-center gap-2"><span>{t.source_city}</span><ArrowRight className="w-3 h-3 text-[#FF5A00]" /><span>{t.destination_city}</span></td>
                      <td className="p-3 text-xs">{t.customer?.name}</td>
                      <td className="p-3">{formatINR(t.money?.customer_freight || 0)}</td>
                      <td className="p-3 text-xs text-zinc-400">{t.created_by_name}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-2">
                          <Link to={`/trips/${t.id}`}>
                            <Button size="sm" variant="outline" className="h-8 rounded-sm border-[#2A2A2A] bg-transparent hover:bg-white/5">View</Button>
                          </Link>
                          <Button size="sm" onClick={() => approveTrip(t.id)} data-testid={`sa-approve-trip-${t.id}`}
                            className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-black font-bold">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {drafts.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Nothing pending — all clear.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="bills" className="mt-6">
            <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr><th className="text-left p-3">Bill #</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Trips</th><th className="text-left p-3">Total</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {bills.map(b => (
                    <tr key={b.id} data-testid={`sa-bill-row-${b.id}`} className="border-t border-[#2A2A2A]">
                      <td className="p-3 font-mono-ll text-xs">{b.bill_no}</td>
                      <td className="p-3 text-xs">{b.customer_name}</td>
                      <td className="p-3 text-xs">{b.trips?.length || 0}</td>
                      <td className="p-3">{formatINR(b.total)}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-2">
                          <Link to={`/prints/bill/${b.id}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="h-8 rounded-sm border-[#2A2A2A] hover:bg-[#FF5A00]/10 text-[#FF5A00]"><Printer className="w-3 h-3 mr-1" /> Preview</Button>
                          </Link>
                          <Button size="sm" onClick={() => approveBill(b.id)} data-testid={`sa-approve-bill-${b.id}`}
                            className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-black font-bold">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteBill(b)} data-testid={`sa-delete-bill-${b.id}`}
                            className="h-8 rounded-sm border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10">
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {bills.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Nothing pending — all clear.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6" data-testid="sa-analytics-panel">
            {analytics ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat label="Outstanding" value={formatINR(analytics.outstanding_receivable_inr || 0)} accent />
                  <Stat label="Delivered OK" value={analytics.pod_outcomes.delivered_ok || 0} />
                  <Stat label="Short Deliveries" value={analytics.pod_outcomes.short_delivery || 0} />
                  <Stat label="Damaged Deliveries" value={analytics.pod_outcomes.damaged_delivery || 0} />
                </div>

                <div className="p-5 border border-[#2A2A2A] bg-[#141414]">
                  <div className="overline text-[#FF5A00] mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Revenue &amp; margin — by month</div>
                  {analytics.revenue_by_month.length === 0 && <div className="text-zinc-500 text-sm py-4">No delivered trips yet.</div>}
                  <div className="space-y-2">
                    {analytics.revenue_by_month.map((r) => {
                      const max = Math.max(...analytics.revenue_by_month.map(x => x.revenue), 1);
                      const w = Math.max(6, Math.round((r.revenue / max) * 100));
                      const mw = Math.max(4, Math.round((Math.max(r.margin, 0) / max) * 100));
                      return (
                        <div key={r.month} className="grid grid-cols-[80px_1fr] items-center gap-3 text-xs">
                          <div className="font-mono-ll text-zinc-400">{r.month || "—"}</div>
                          <div>
                            <div className="h-4 bg-[#FF5A00]/80" style={{ width: `${w}%` }} title={`Revenue: ${formatINR(r.revenue)}`}></div>
                            <div className="h-2 bg-emerald-500/70 mt-1" style={{ width: `${mw}%` }} title={`Margin: ${formatINR(r.margin)}`}></div>
                            <div className="flex justify-between text-[10px] text-zinc-500 mt-0.5">
                              <span>Rev {formatINR(r.revenue)}</span><span>Margin {formatINR(r.margin)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-5 border border-[#2A2A2A] bg-[#141414]">
                    <div className="overline text-[#FF5A00] mb-3">Top routes</div>
                    <div className="text-sm">
                      {analytics.top_routes.length === 0 && <div className="text-zinc-500 py-2">No data yet.</div>}
                      {analytics.top_routes.map((r) => (
                        <div key={r.route} className="flex justify-between py-1 border-b border-[#2A2A2A] last:border-b-0">
                          <span className="font-mono-ll text-xs">{r.route}</span>
                          <span className="text-[#FF5A00] font-bold">{r.trips}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-5 border border-[#2A2A2A] bg-[#141414]">
                    <div className="overline text-[#FF5A00] mb-3">Top customers</div>
                    <div className="text-sm">
                      {analytics.top_customers.length === 0 && <div className="text-zinc-500 py-2">No data yet.</div>}
                      {analytics.top_customers.map((c) => (
                        <div key={c.name} className="flex justify-between py-1 border-b border-[#2A2A2A] last:border-b-0">
                          <span className="text-xs">{c.name}</span>
                          <span className="font-mono-ll text-emerald-400">{formatINR(c.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-5 border border-[#2A2A2A] bg-[#141414]">
                  <div className="overline text-[#FF5A00] mb-3">Bill status breakdown</div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                    <div><div className="text-xs text-zinc-500 uppercase">Pending</div><div className="font-display font-black text-xl text-amber-400">{analytics.bills.pending_approval || 0}</div></div>
                    <div><div className="text-xs text-zinc-500 uppercase">Approved</div><div className="font-display font-black text-xl text-sky-400">{analytics.bills.approved || 0}</div></div>
                    <div><div className="text-xs text-zinc-500 uppercase">Partial</div><div className="font-display font-black text-xl text-orange-400">{analytics.bills.partial || 0}</div></div>
                    <div><div className="text-xs text-zinc-500 uppercase">Paid</div><div className="font-display font-black text-xl text-emerald-400">{analytics.bills.paid || 0}</div></div>
                    <div><div className="text-xs text-zinc-500 uppercase">Cancelled</div><div className="font-display font-black text-xl text-zinc-500">{analytics.bills.cancelled || 0}</div></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-zinc-500 py-10">Loading analytics…</div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-[#2A2A2A] bg-[#141414]"}`}>
      <div className="overline text-zinc-400">{label}</div>
      <div className={`font-display font-black text-2xl mt-2 ${accent ? "text-[#FF5A00]" : ""}`}>{value}</div>
    </div>
  );
}
